// Single source of truth for the app. Wraps a signal + history + actions.
//
// Patterns:
//   - actions are functions, not methods on a class
//   - all state updates go through `setState` (immutable)
//   - "discrete" actions auto-snapshot before mutating
//   - "continuous" edits (slider drags) call beginEdit/endEdit to coalesce
//   - autosave is opt-in via startAutosave() so tests don't leak timers

import { signal, computed, effect, type ReadSignal } from './signals';
import {
  INITIAL_STATE,
  STATE_VERSION,
  type AppState,
  type Layer,
  type Resolution,
  type Theme,
} from './types';
import * as history from './history';

const STATE_KEY = 'pl.state.v1';
const THEME_KEY = 'pl.theme';

// ---- ID generation ---------------------------------------------------------

export function makeLayerId(): string {
  // 7 chars of base36 randomness — collision-free in practice for local stacks.
  return `l${Math.random().toString(36).slice(2, 9)}`;
}

// ---- Persistence -----------------------------------------------------------

type PersistedState = {
  v: typeof STATE_VERSION;
  resolution: Resolution;
  layers: Layer[];
  selectedLayerId: string | null;
};

function safeLocalStorage(): Storage | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

function loadInitialState(): AppState {
  const ls = safeLocalStorage();
  if (!ls) return cloneInitial();
  try {
    const raw = ls.getItem(STATE_KEY);
    if (!raw) return cloneInitial();
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    if (parsed.v !== STATE_VERSION) return cloneInitial();
    return {
      ...cloneInitial(),
      resolution: parsed.resolution ?? INITIAL_STATE.resolution,
      layers: Array.isArray(parsed.layers) ? parsed.layers : [],
      selectedLayerId: parsed.selectedLayerId ?? null,
    };
  } catch {
    return cloneInitial();
  }
}

function cloneInitial(): AppState {
  return {
    resolution: { ...INITIAL_STATE.resolution },
    layers: [],
    selectedLayerId: null,
    soloLayerId: null,
    time: 0,
    paused: true,
    fps: 8,
  };
}

// ---- Store -----------------------------------------------------------------

const _state = signal<AppState>(loadInitialState());
const _history = signal<history.HistoryStacks>(history.emptyHistory());
const _theme = signal<Theme>(loadInitialTheme());

export const state: ReadSignal<AppState> = {
  get: _state.get.bind(_state),
  peek: _state.peek.bind(_state),
};

export const theme: ReadSignal<Theme> = {
  get: _theme.get.bind(_theme),
  peek: _theme.peek.bind(_theme),
};

export const selectedLayer = computed<Layer | null>(() => {
  const s = _state.get();
  if (s.selectedLayerId === null) return null;
  return s.layers.find((l) => l.id === s.selectedLayerId) ?? null;
});

export const canUndo = computed(() => history.canUndo(_history.get()));
export const canRedo = computed(() => history.canRedo(_history.get()));

// ---- Edit transactions -----------------------------------------------------

let editSnapshot: AppState | null = null;

/** Mark the start of a coalesced edit (e.g. slider drag). */
export function beginEdit(): void {
  if (editSnapshot !== null) return; // already in an edit
  editSnapshot = _state.peek();
}

/** Commit a coalesced edit. Pushes one history entry if state changed. */
export function endEdit(): void {
  if (editSnapshot === null) return;
  const before = editSnapshot;
  editSnapshot = null;
  if (before !== _state.peek()) {
    _history.set(history.push(_history.peek(), before));
  }
}

/** Discrete actions auto-snapshot. */
function autoSnapshot(): void {
  if (editSnapshot !== null) return; // inside an edit; snapshot on commit
  _history.set(history.push(_history.peek(), _state.peek()));
}

function setState(next: AppState): void {
  _state.set(next);
}

// ---- Actions ---------------------------------------------------------------

export function addLayer(partial: Omit<Layer, 'id'>): string {
  autoSnapshot();
  const id = makeLayerId();
  const layer: Layer = { ...partial, id };
  const s = _state.peek();
  setState({ ...s, layers: [...s.layers, layer], selectedLayerId: id });
  return id;
}

export function insertLayer(partial: Omit<Layer, 'id'>, atIndex: number): string {
  autoSnapshot();
  const id = makeLayerId();
  const layer: Layer = { ...partial, id };
  const s = _state.peek();
  const i = clamp(atIndex, 0, s.layers.length);
  const layers = [...s.layers.slice(0, i), layer, ...s.layers.slice(i)];
  setState({ ...s, layers, selectedLayerId: id });
  return id;
}

export function removeLayer(id: string): void {
  const s = _state.peek();
  if (!s.layers.some((l) => l.id === id)) return;
  autoSnapshot();
  const layers = s.layers.filter((l) => l.id !== id);
  const selectedLayerId =
    s.selectedLayerId === id ? (layers[layers.length - 1]?.id ?? null) : s.selectedLayerId;
  setState({ ...s, layers, selectedLayerId });
}

export function moveLayer(id: string, toIndex: number): void {
  const s = _state.peek();
  const from = s.layers.findIndex((l) => l.id === id);
  if (from < 0) return;
  const to = clamp(toIndex, 0, s.layers.length - 1);
  if (from === to) return;
  autoSnapshot();
  const layers = [...s.layers];
  const [moved] = layers.splice(from, 1);
  if (moved) layers.splice(to, 0, moved);
  setState({ ...s, layers });
}

export function updateLayer(id: string, patch: Partial<Omit<Layer, 'id'>>): void {
  const s = _state.peek();
  const i = s.layers.findIndex((l) => l.id === id);
  if (i < 0) return;
  const current = s.layers[i] as Layer;
  const params = patch.params ? { ...current.params, ...patch.params } : current.params;
  const next: Layer = { ...current, ...patch, params, id: current.id };
  if (shallowLayerEqual(current, next)) return;
  if (editSnapshot === null) autoSnapshot();
  const layers = [...s.layers];
  layers[i] = next;
  setState({ ...s, layers });
}

export function toggleLayer(id: string): void {
  const s = _state.peek();
  const layer = s.layers.find((l) => l.id === id);
  if (!layer) return;
  updateLayer(id, { enabled: !layer.enabled });
}

export function selectLayer(id: string | null): void {
  // selection changes do NOT push history
  const s = _state.peek();
  if (s.selectedLayerId === id) return;
  setState({ ...s, selectedLayerId: id });
}

export function setResolution(res: Resolution): void {
  const s = _state.peek();
  if (s.resolution.w === res.w && s.resolution.h === res.h) return;
  autoSnapshot();
  setState({ ...s, resolution: res });
}

export function setTime(t: number): void {
  const s = _state.peek();
  const time = Math.max(0, Math.floor(t));
  if (s.time === time) return;
  setState({ ...s, time });
}

export function tickTime(): void {
  setTime(_state.peek().time + 1);
}

export function setPaused(paused: boolean): void {
  const s = _state.peek();
  if (s.paused === paused) return;
  setState({ ...s, paused });
}

export function setFps(fps: number): void {
  const s = _state.peek();
  const next = Math.max(1, Math.min(60, Math.floor(fps)));
  if (s.fps === next) return;
  setState({ ...s, fps: next });
}

// ---- Solo / isolate --------------------------------------------------------

export function setSolo(id: string | null): void {
  const s = _state.peek();
  if (s.soloLayerId === id) return;
  setState({ ...s, soloLayerId: id });
}

export function toggleSolo(id: string): void {
  const s = _state.peek();
  setSolo(s.soloLayerId === id ? null : id);
}

// ---- Playback (animation) --------------------------------------------------

let playTimer: ReturnType<typeof setInterval> | null = null;

export function play(): void {
  if (playTimer !== null) return;
  setPaused(false);
  const tick = (): void => {
    if (_state.peek().paused) {
      pause();
      return;
    }
    setTime(_state.peek().time + 1);
  };
  const fps = Math.max(1, Math.min(60, _state.peek().fps));
  playTimer = setInterval(tick, 1000 / fps);
}

export function pause(): void {
  if (playTimer !== null) {
    clearInterval(playTimer);
    playTimer = null;
  }
  setPaused(true);
}

export function togglePlay(): void {
  if (_state.peek().paused) play();
  else pause();
}

// ---- Undo / redo -----------------------------------------------------------

export function undo(): boolean {
  const result = history.undo(_history.peek(), _state.peek());
  if (!result) return false;
  _state.set(result.state);
  _history.set(result.history);
  return true;
}

export function redo(): boolean {
  const result = history.redo(_history.peek(), _state.peek());
  if (!result) return false;
  _state.set(result.state);
  _history.set(result.history);
  return true;
}

export function clearHistory(): void {
  _history.set(history.clear());
}

// ---- Theme -----------------------------------------------------------------

function loadInitialTheme(): Theme {
  const ls = safeLocalStorage();
  if (!ls) return 'dark';
  try {
    const v = ls.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'dark';
  } catch {
    return 'dark';
  }
}

export function setTheme(t: Theme): void {
  if (_theme.peek() === t) return;
  _theme.set(t);
  const ls = safeLocalStorage();
  if (ls) {
    try {
      ls.setItem(THEME_KEY, t);
    } catch {
      // quota or privacy mode — ignore
    }
  }
  if (typeof document !== 'undefined') {
    document.documentElement.dataset.theme = t;
  }
}

export function toggleTheme(): void {
  setTheme(_theme.peek() === 'dark' ? 'light' : 'dark');
}

// ---- Whole-state setter (used by URL hash sync, presets, undo migration) ---

export function replaceState(next: AppState, recordHistory = true): void {
  if (recordHistory) {
    _history.set(history.push(_history.peek(), _state.peek()));
  }
  setState(next);
}

// ---- Autosave --------------------------------------------------------------

export function startAutosave(debounceMs = 300): () => void {
  const ls = safeLocalStorage();
  if (!ls) return () => {};
  let timer: ReturnType<typeof setTimeout> | null = null;
  const dispose = effect(() => {
    const s = _state.get();
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      try {
        const persisted: PersistedState = {
          v: STATE_VERSION,
          resolution: s.resolution,
          layers: s.layers,
          selectedLayerId: s.selectedLayerId,
        };
        ls.setItem(STATE_KEY, JSON.stringify(persisted));
      } catch {
        // ignore storage failures
      }
    }, debounceMs);
  });
  return () => {
    dispose();
    if (timer !== null) clearTimeout(timer);
  };
}

// ---- Helpers ---------------------------------------------------------------

function clamp(n: number, min: number, max: number): number {
  return n < min ? min : n > max ? max : n;
}

function shallowLayerEqual(a: Layer, b: Layer): boolean {
  if (a === b) return true;
  if (a.kind !== b.kind || a.blend !== b.blend || a.opacity !== b.opacity) return false;
  if (a.enabled !== b.enabled || a.seed !== b.seed) return false;
  if (a.params === b.params) return true;
  const ka = Object.keys(a.params);
  const kb = Object.keys(b.params);
  if (ka.length !== kb.length) return false;
  for (const k of ka) if (a.params[k] !== b.params[k]) return false;
  return true;
}

// ---- Test-only reset (not part of public surface) -------------------------

/** Reset the store to initial. Internal use only — for tests. */
export function __resetForTests(): void {
  editSnapshot = null;
  _state.set(cloneInitial());
  _history.set(history.emptyHistory());
  _theme.set('dark');
}

// re-export blend modes / kinds for convenience
export type { Layer, LayerKind, BlendMode, Resolution, Theme, AppState } from './types';
